<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { ElMessage } from "element-plus";

import { ReplyTypesDict } from "../api/models";
import type { AvatarItem, Subscription, WechatAccountDetail } from "../api/models";
import { getWechatAccounts, getWechatUser } from "../api/http";
import { messageTypesDict } from "../api/util";
import AvatarSelector from "./AvatarSelector.vue";
import AvatarMultipleSelector from "./AvatarMultipleSelector.vue";

const props = defineProps<{ subscription: Subscription | null }>();
const emit = defineEmits<{ (e: "update:subscription", value: Subscription | null): void }>();

const wechatMap: Record<string, WechatAccountDetail> = {};

const wechats = ref<Array<AvatarItem>>([]);
const friends = ref<Array<AvatarItem>>([]);
const chatrooms = ref<Array<AvatarItem>>([]);
const replyTypes = ref<Array<{ label: string; value: number }>>([]);

ReplyTypesDict.forEach((item: string, index: number) => {
  replyTypes.value.push({ label: item, value: index });
});

const loadWechatInfo = (id: string) => {
  if (!id) return;
  if (wechatMap[id]) {
    loadFriendsAndChatrooms(wechatMap[id]);
  } else {
    getWechatUser(id).then(res => {
      if (res.successful) {
        wechatMap[id] = res.result;
        loadFriendsAndChatrooms(wechatMap[id]);
      }
    });
  }
}

const loadFriendsAndChatrooms = (wechat: WechatAccountDetail) => {
  // Drop entries with no `v1` — those become el-option entries with
  // `:value="undefined"` which Vue warns about (patchDOMProp on the
  // option's value attribute). Fall back to id for the display name
  // when nickName is missing, and to empty string for imageUrl so
  // el-image renders its error slot cleanly instead of binding
  // `src="undefined"`.
  const toAvatarItem = (c: { v1: string; nickName?: string; smallHead?: string }) => ({
    id: c.v1,
    name: c.nickName || c.v1,
    imageUrl: c.smallHead || "",
  });
  friends.value = (wechat.friends ?? []).filter((f) => !!f.v1).map(toAvatarItem);
  chatrooms.value = (wechat.chatrooms ?? []).filter((c) => !!c.v1).map(toAvatarItem);
}

const subscription = computed<Subscription | null>({
  get() {
    return props.subscription;
  },
  set(value) {
    emit('update:subscription', value);
    if (value) loadWechatInfo(value.wechatUserId);
  }
});

watch(() => subscription.value?.wechatUserId, (id) => {
  if (id) loadWechatInfo(id);
});

const loadWechatAccounts = () => {
  getWechatAccounts().then(res => {
    if (res.successful) {
      wechats.value = res.result.map(item => ({
        id: item.wcId,
        name: item.nickName,
        imageUrl: item.smallHeadImgUrl,
      }));
    } else {
      ElMessage({
        message: res.message,
        type: "error",
      });
    }
  });
}

loadWechatAccounts();
if (subscription.value) loadWechatInfo(subscription.value.wechatUserId);
</script>

<template>
  <template v-if="subscription">
    <el-row :gutter="20" class="row">
      <el-col :span="6">微信账号</el-col>
      <el-col :span="18">
        <AvatarSelector v-model:items="wechats" v-model:id="subscription.wechatUserId"></AvatarSelector>
      </el-col>
    </el-row>
    <el-row :gutter="20" class="row">
      <el-col :span="6">来自用户</el-col>
      <el-col :span="18">
        <AvatarMultipleSelector v-model:items="friends" v-model:ids="subscription.fromUsers"></AvatarMultipleSelector>
      </el-col>
    </el-row>
    <el-row :gutter="20" class="row">
      <el-col :span="6">来自群聊</el-col>
      <el-col :span="18">
        <AvatarMultipleSelector v-model:items="chatrooms" v-model:ids="subscription.fromGroups"></AvatarMultipleSelector>
      </el-col>
    </el-row>
    <el-row :gutter="20" class="row">
      <el-col :span="6">关键词</el-col>
      <el-col :span="18">
        <el-input v-model="subscription.keyword" />
      </el-col>
    </el-row>
    <el-row :gutter="20" class="row">
      <el-col :span="6">@</el-col>
      <el-col :span="18">
        <el-checkbox v-model="subscription.at" label="at" size="large" />
      </el-col>
    </el-row>
    <el-row :gutter="20" class="row">
      <el-col :span="6">回复类型</el-col>
      <el-col :span="18">
        <el-select v-model="subscription.replyType" class="m-2" placeholder="请选择" size="large">
          <el-option v-for="item in replyTypes" :key="item.value" :label="item.label" :value="item.value" />
        </el-select>
      </el-col>
    </el-row>
    <el-row :gutter="20" class="row">
      <el-col :span="6">内容</el-col>
      <el-col :span="18">
        <el-input v-model="subscription.content" />
      </el-col>
    </el-row>
    <el-row :gutter="20" class="row">
      <el-col :span="6">消息类型</el-col>
      <el-col :span="18">
        <el-checkbox-group v-model="subscription.messageTypes">
          <el-checkbox v-for="(value, key) in messageTypesDict" :key="key" :label="key">{{ value }}</el-checkbox>
        </el-checkbox-group>
      </el-col>
    </el-row>
  </template>
</template>

<style scoped>

</style>
